import { Decimal as DecimalJs } from "decimal.js";
/**
 * Projenin TEK yuvarlama politikası (AGENTS.md §3.2): ROUND_HALF_UP.
 * decimal.js'te bu mod "yarımı sıfırdan uzağa" yuvarlar: 0.5 → 1, -0.5 → -1 (simetrik).
 */
export const ROUNDING_MODE = DecimalJs.ROUND_HALF_UP;
/** Ondalık hassasiyet sabitleri — veritabanı sütun tipleriyle birebir eşleşir. */
export const SCALE = {
    /** Miktar: litre, kWh, kg, adet — numeric(18,3) */
    QUANTITY: 3,
    /** Birim fiyat — numeric(18,4) */
    UNIT_PRICE: 4,
    /** Oran (yüzde) — numeric(9,4) */
    RATE: 4,
    /** Para: kuruş tam sayıdır */
    MONEY_MINOR: 0,
};
// Global Decimal ayarlarını kirletmemek için yalıtılmış kopya.
const D = DecimalJs.clone({ precision: 60, rounding: ROUNDING_MODE, toExpNeg: -40, toExpPos: 60 });
const DECIMAL_STRING = /^[+-]?(\d+)(\.\d+)?$/;
/**
 * Değişmez (immutable) ondalık sayı. Miktar, birim fiyat ve oranlar için kullanılır.
 * `number` yalnızca GÜVENLİ TAM SAYI ise kabul edilir; kayan noktalı değerler bilinçli olarak reddedilir.
 */
export class Decimal {
    value;
    constructor(value) {
        this.value = value;
    }
    static from(input) {
        if (input instanceof Decimal)
            return input;
        if (typeof input === "bigint")
            return new Decimal(new D(input.toString()));
        if (typeof input === "number") {
            if (!Number.isSafeInteger(input)) {
                throw new RangeError(`Decimal.from: kayan noktalı/güvensiz sayı kabul edilmez (${input}). Dizge kullanın: Decimal.from("12.345")`);
            }
            return new Decimal(new D(input));
        }
        const text = input.trim();
        if (!DECIMAL_STRING.test(text)) {
            throw new RangeError(`Decimal.from: geçersiz ondalık dizge "${input}"`);
        }
        return new Decimal(new D(text));
    }
    /** Türkçe biçimli girdiyi ayrıştırır: "1.234,56" → 1234.56 */
    static parseTr(input) {
        const text = input.trim().replace(/\s/g, "");
        if (!/^[+-]?(\d{1,3}(\.\d{3})+|\d+)(,\d+)?$/.test(text)) {
            throw new RangeError(`Decimal.parseTr: geçersiz sayı "${input}"`);
        }
        return Decimal.from(text.replace(/\./g, "").replace(",", "."));
    }
    static ZERO = new Decimal(new D(0));
    static ONE = new Decimal(new D(1));
    static HUNDRED = new Decimal(new D(100));
    static sum(values) {
        let total = Decimal.ZERO;
        for (const value of values)
            total = total.add(value);
        return total;
    }
    static min(first, ...rest) {
        return rest.reduce((acc, value) => (Decimal.from(value).lt(acc) ? Decimal.from(value) : acc), Decimal.from(first));
    }
    static max(first, ...rest) {
        return rest.reduce((acc, value) => (Decimal.from(value).gt(acc) ? Decimal.from(value) : acc), Decimal.from(first));
    }
    add(other) {
        return new Decimal(this.value.plus(Decimal.from(other).value));
    }
    sub(other) {
        return new Decimal(this.value.minus(Decimal.from(other).value));
    }
    mul(other) {
        return new Decimal(this.value.times(Decimal.from(other).value));
    }
    /** Bölme sonucu 60 anlamlı basamakla tutulur; saklamadan önce `round` ile ölçeğe indirilmelidir. */
    div(other) {
        const divisor = Decimal.from(other);
        if (divisor.isZero())
            throw new RangeError("Decimal.div: sıfıra bölme");
        return new Decimal(this.value.div(divisor.value));
    }
    neg() {
        return new Decimal(this.value.neg());
    }
    abs() {
        return new Decimal(this.value.abs());
    }
    /** Verilen ondalık basamağa ROUND_HALF_UP ile yuvarlar. */
    round(scale) {
        return new Decimal(this.value.toDecimalPlaces(scale, ROUNDING_MODE));
    }
    /** Aşağı (−∞ yönüne) yuvarlar; kalan dağıtımı algoritmalarında kullanılır. */
    floor(scale = 0) {
        return new Decimal(this.value.toDecimalPlaces(scale, DecimalJs.ROUND_FLOOR));
    }
    cmp(other) {
        return this.value.cmp(Decimal.from(other).value);
    }
    eq(other) {
        return this.cmp(other) === 0;
    }
    lt(other) {
        return this.cmp(other) < 0;
    }
    lte(other) {
        return this.cmp(other) <= 0;
    }
    gt(other) {
        return this.cmp(other) > 0;
    }
    gte(other) {
        return this.cmp(other) >= 0;
    }
    isZero() {
        return this.value.isZero();
    }
    isNegative() {
        return this.value.isNegative() && !this.value.isZero();
    }
    isPositive() {
        return this.value.isPositive() && !this.value.isZero();
    }
    isInteger() {
        return this.value.isInteger();
    }
    /** Tam sayıya çevirir; kesirli değerde hata verir (sessiz kırpma yok). */
    toBigInt() {
        if (!this.value.isInteger())
            throw new RangeError(`Decimal.toBigInt: tam sayı değil (${this.toString()})`);
        return BigInt(this.value.toFixed(0));
    }
    /** Sabit basamaklı dizge; veritabanına numeric olarak yazmak için. Yuvarlama ROUND_HALF_UP. */
    toFixed(scale) {
        return this.value.toFixed(scale, ROUNDING_MODE);
    }
    toString() {
        return this.value.toFixed();
    }
    /** JSON'da her zaman dizge olarak taşınır (float'a dönüşmesin). */
    toJSON() {
        return this.toString();
    }
    /** Türkiye biçimi: 1.234,560 */
    formatTr(scale) {
        const fixed = this.abs().toFixed(scale);
        const [integerPart = "0", fractionPart] = fixed.split(".");
        const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        const sign = this.round(scale).isNegative() ? "-" : "";
        return `${sign}${grouped}${fractionPart ? `,${fractionPart}` : ""}`;
    }
}
//# sourceMappingURL=decimal.js.map